import { FC, useMemo } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { useFormContext } from 'react-hook-form';
import { MenuItem, Select } from '@mui/material';
import Switch, { SwitchProps } from '@mui/material/Switch';
import { styled } from '@mui/material/styles';

import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { ChargeDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum } from 'src/utils/utility';
import { NoContractForm } from './types';
import { CHARGE_CATEGORIES, LEG_TYPES } from '../../contractManager/rateCardCharges/constants';

// Styled switch with green for checked and red for unchecked
const DecisionSwitch = styled((props: SwitchProps) => <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />)(({ theme }) => ({
  width: 52,
  height: 32,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 4,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#00A651',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#00A651',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 24,
    height: 24,
  },
  '& .MuiSwitch-track': {
    borderRadius: 32 / 2,
    backgroundColor: '#E72547',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

interface ITableSpotShipmentProps {
  data: ChargeDto[];
  isEditable?: boolean;
}

interface SpotShipmentWithEditable extends Omit<ChargeDto, 'legType'> {
  chargeCategory?: string;
  legType?: string;
  decision?: boolean;
}

export const TableSpotShipment: FC<ITableSpotShipmentProps> = ({ data = [], isEditable = false }) => {
  const { watch, setValue } = useFormContext<NoContractForm>();
  const formValues = watch();

  const columns = useMemo<MRT_ColumnDef<SpotShipmentWithEditable>[]>(
    () => [
      {
        accessorKey: 'chargeName',
        header: 'Charge name',
        enableEditing: false,
        size: 200,
        Cell: ({ row }) => <div className='text-14 xl:text-16'>{row.original.chargeName || '-'}</div>,
      },
      {
        accessorKey: 'chargeCategory',
        header: 'Charge Category',
        enableEditing: false,
        size: 200,
        Cell: ({ row }) => {
          const index = data.findIndex((item) => item.id === row.original.id);
          const categoryValue = formValues?.invoiceCharges?.[index]?.chargeCategory || row.original.chargeCategory;

          if (isEditable) {
            return (
              <Select
                value={categoryValue || ''}
                onChange={(e) => {
                  const currentCharges = formValues?.invoiceCharges || [];
                  const updatedCharges = [...currentCharges];
                  updatedCharges[index] = {
                    ...updatedCharges[index],
                    ...row.original,
                    chargeCategory: e.target.value,
                  };
                  setValue('invoiceCharges', updatedCharges, { shouldValidate: true });
                }}
                size='small'
                fullWidth
                displayEmpty
              >
                <MenuItem value='' disabled>
                  Select category
                </MenuItem>
                {CHARGE_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {beautifyEnum(category)}
                  </MenuItem>
                ))}
              </Select>
            );
          }

          return <div className='text-14 xl:text-16 capitalize'>{categoryValue ? beautifyEnum(categoryValue) : '-'}</div>;
        },
      },
      {
        accessorKey: 'legType',
        header: 'Leg Type',
        enableEditing: false,
        size: 180,
        Cell: ({ row }) => {
          const index = data.findIndex((item) => item.id === row.original.id);
          const legTypeValue = formValues?.invoiceCharges?.[index]?.legType || row.original.legType;

          if (isEditable) {
            return (
              <Select
                value={legTypeValue || ''}
                onChange={(e) => {
                  const currentCharges = formValues?.invoiceCharges || [];
                  const updatedCharges = [...currentCharges];
                  updatedCharges[index] = {
                    ...updatedCharges[index],
                    ...row.original,
                    legType: e.target.value,
                  };
                  setValue('invoiceCharges', updatedCharges, { shouldValidate: true });
                }}
                size='small'
                fullWidth
                displayEmpty
              >
                <MenuItem value='' disabled>
                  Select leg type
                </MenuItem>
                {LEG_TYPES.map((legType) => (
                  <MenuItem key={legType} value={legType}>
                    {beautifyEnum(legType)}
                  </MenuItem>
                ))}
              </Select>
            );
          }

          return <div className='text-14 xl:text-16 capitalize'>{legTypeValue ? beautifyEnum(legTypeValue) : '-'}</div>;
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'UNIT PRICE',
        enableEditing: false,
        size: 180,
        Cell: ({ row }) => {
          const { amountBeforeTaxInOriginalCurrency, originalCurrency, quantity } = row.original;

          if (!amountBeforeTaxInOriginalCurrency) return <div className='text-14 xl:text-16'>-</div>;

          const unitPrice = quantity ? (amountBeforeTaxInOriginalCurrency / quantity).toFixed(2) : amountBeforeTaxInOriginalCurrency.toFixed(2);

          return <div className='text-14 xl:text-16'>{`${originalCurrency} ${unitPrice}`}</div>;
        },
      },
      {
        accessorKey: 'decision',
        header: 'Decision',
        enableEditing: false,
        size: 140,
        Cell: ({ row }) => {
          const index = data.findIndex((item) => item.id === row.original.id);
          const decisionValue = formValues?.invoiceCharges?.[index]?.decision ?? true;

          if (isEditable) {
            return (
              <div className='flex justify-center'>
                <DecisionSwitch
                  checked={decisionValue}
                  onChange={(e) => {
                    const currentCharges = formValues?.invoiceCharges || [];
                    const updatedCharges = [...currentCharges];
                    updatedCharges[index] = {
                      ...updatedCharges[index],
                      ...row.original,
                      decision: e.target.checked,
                    };
                    setValue('invoiceCharges', updatedCharges, { shouldValidate: true });
                  }}
                />
              </div>
            );
          }

          return (
            <div className='flex justify-center'>
              <DecisionSwitch checked={decisionValue} disabled />
            </div>
          );
        },
      },
    ],
    [data, isEditable, formValues?.invoiceCharges, setValue]
  );

  const table = useMaterialReactTable({
    enableSorting: true,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    enableRowSelection: false,
    enableMultiRowSelection: false,
    data,
    columns,
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: 'rgb(242, 242, 242)!important',
        },
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#F5F6FA',
        fontWeight: 600,
        fontSize: '14px',
        color: '#4A4A4A',
        textTransform: 'uppercase',
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: '12px 16px',
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
  });

  return <CustomTable table={table} />;
};
